
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import BabySelector from '../base/BabySelector';
import React from 'react';

interface TopNavProps {
  title: string;
  /** Show the back button. Defaults to false. */
  showBackButton?: boolean;
  /** Callback when back button is pressed. If omitted, navigation goes back one step. */
  onBack?: () => void;
  /** Show the baby selector component. Defaults to false. */
  showBabySelector?: boolean;
  /** Show the admin/dashboard button. Defaults to false. */
  showAdminButton?: boolean;
}

/**
 * Top navigation bar used across the application.
 *
 * Features:
 * - Optional back button (custom handler or default history back)
 * - Optional admin button that navigates to `/admin`
 * - Sign‑out button that logs the user out and redirects to `/auth`
 * - Optional baby selector displayed underneath the main bar
 */
export default function TopNav({
  title,
  showBackButton = false,
  onBack,
  showBabySelector = false,
  showAdminButton = false,
}: TopNavProps) {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  /**
   * Handles user sign‑out.
   * Errors are caught and logged so the UI does not break silently.
   */
  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Error signing out:', error);
        return;
      }
      // Force navigation to auth page
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      // Force navigation even if there's an error
      navigate('/auth', { replace: true });
    }
  };

  const navigateToAdmin = () => {
    navigate('/admin');
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Volver"
                aria-label="Back"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
            )}
            <h1 className="text-lg font-semibold text-gray-800 truncate">{title}</h1>
          </div>

          <div className="flex items-center gap-2">
            {showAdminButton && (
              <button
                onClick={navigateToAdmin}
                className="p-2 hover:bg-blue-50 rounded-full transition-colors"
                title="Dashboard Administrativo"
                aria-label="Admin Dashboard"
              >
                <i className="ri-dashboard-3-line text-xl text-blue-600"></i>
              </button>
            )}

            {/* Tu cuenta: compartir el bebé, el informe del pediatra,
                descargar los datos y borrar la cuenta. Va acá porque es
                la única barra que aparece en todas las pantallas. */}
            <button
              onClick={() => navigate('/cuenta')}
              className="p-2 hover:bg-indigo-50 rounded-full transition-colors"
              title="Tu cuenta"
              aria-label="Tu cuenta"
            >
              <i className="ri-user-settings-line text-xl text-indigo-600"></i>
            </button>

            <button
              onClick={handleSignOut}
              className="p-2 hover:bg-red-50 rounded-full transition-colors"
              title="Cerrar sesión"
              aria-label="Sign out"
            >
              <i className="ri-logout-box-r-line text-xl text-red-600"></i>
            </button>
          </div>
        </div>

        {showBabySelector && (
          <div className="mt-3">
            <BabySelector />
          </div>
        )}
      </div>
    </div>
  );
}
